import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton,
  ListItemButton, ListItemIcon, ListItemText, Avatar,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  CalendarMonth, People, ContentCut,
  AccessTime, Person, BarChart, Business, Logout,
  ChevronLeft,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BRAND_GRADIENT } from '../theme';

const DRAWER_WIDTH = 280;

const NAV_ITEMS = [
  {
    label: 'Agenda',
    icon:  <CalendarMonth />,
    path:  '/agenda',
    roles: ['SuperAdmin', 'AdminNegocio', 'Recepcionista', 'Profesional'],
  },
  {
    label: 'Agenda negocio',
    icon:  <CalendarMonth />,
    path:  '/negocio/agenda',
    roles: ['SuperAdmin', 'AdminNegocio', 'Recepcionista'],
  },
  {
    label: 'Clientes',
    icon:  <People />,
    path:  '/clientes',
    roles: ['SuperAdmin', 'AdminNegocio', 'Recepcionista', 'Profesional'],
  },
  {
    label: 'Servicios',
    icon:  <ContentCut />,
    path:  '/servicios',
    roles: ['SuperAdmin', 'AdminNegocio', 'Profesional'],
  },
  {
    label: 'Disponibilidad',
    icon:  <AccessTime />,
    path:  '/disponibilidad',
    roles: ['SuperAdmin', 'AdminNegocio', 'Profesional'],
  },
  {
    label: 'Estadísticas',
    icon:  <BarChart />,
    path:  '/estadisticas',
    roles: ['SuperAdmin', 'AdminNegocio', 'Profesional'],
  },
  {
    label: 'Mi negocio',
    icon:  <Business />,
    path:  '/negocio/admin',
    roles: ['SuperAdmin', 'AdminNegocio'],
  },
  {
    label: 'Mi perfil',
    icon:  <Person />,
    path:  '/perfil',
    roles: ['SuperAdmin', 'AdminNegocio', 'Recepcionista', 'Profesional'],
  },
];

/* ── Contenido del Drawer ─────────────────────────────────── */
function DrawerContent({ user, navItems, location, onNav, onClose, onLogout }) {
  return (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: BRAND_GRADIENT,
      }}
    >
      {/* Cabecera */}
      <Box
        sx={{
          p: 2.5,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', letterSpacing: -0.5, lineHeight: 1.1 }}>
            MiSD<span style={{ fontWeight: 300, opacity: 0.85 }}>Agenda</span>
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}>
            Sistema de agendamiento
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
          <ChevronLeft />
        </IconButton>
      </Box>

      {/* Nav items */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: 1 }}>
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              onClick={() => onNav(item.path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.1,
                px: 1.5,
                color: isActive ? '#fff' : 'rgba(255,255,255,0.72)',
                bgcolor: isActive ? 'rgba(255,255,255,0.18)' : 'transparent',
                fontWeight: isActive ? 700 : 400,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                  color: '#fff',
                },
                transition: 'all 0.15s ease',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: isActive ? 700 : 500,
                }}
              />
              {isActive && (
                <Box
                  sx={{
                    width: 4, height: 24, borderRadius: 2,
                    bgcolor: '#fff', ml: 1, opacity: 0.9,
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </Box>

      {/* Footer — usuario */}
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <ListItemButton
          onClick={onLogout}
          sx={{
            borderRadius: 2, py: 1.2,
            color: 'rgba(255,255,255,0.72)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
          }}
        >
          <Avatar
            sx={{
              width: 32, height: 32, mr: 1.5,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {user?.nombre?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#fff', fontSize: '0.82rem' }}>
              {user?.nombre}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem' }}>
              {user?.rol}
            </Typography>
          </Box>
          <Tooltip title="Cerrar sesión">
            <Logout sx={{ fontSize: 18, opacity: 0.6 }} />
          </Tooltip>
        </ListItemButton>
      </Box>
    </Box>
  );
}

/* ── AppLayout principal ──────────────────────────────────── */
export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = NAV_ITEMS.filter(item => item.roles.includes(user?.rol));

  const handleNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawerProps = {
    user, navItems, location,
    onNav:    handleNav,
    onClose:  () => setDrawerOpen(false),
    onLogout: handleLogout,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── AppBar siempre visible ── */}
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(o => !o)}
            sx={{ mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
            MiSD<span style={{ fontWeight: 300, opacity: 0.85 }}>Agenda</span>
          </Typography>
          <Box flex={1} />
          {/* Nombre de usuario en topbar */}
          <Typography variant="body2" sx={{ opacity: 0.8, display: { xs: 'none', sm: 'block' } }}>
            {user?.nombre}
          </Typography>
          <Avatar
            sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setDrawerOpen(o => !o)}
          >
            {user?.nombre?.[0]}
          </Avatar>
        </Toolbar>
      </AppBar>

      {/* ── Drawer (temporal — siempre hamburger) ── */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none', boxShadow: '4px 0 24px rgba(0,0,0,0.2)' } }}
      >
        <DrawerContent {...drawerProps} />
      </Drawer>

      {/* ── Contenido principal ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',   // altura del AppBar
          p:  { xs: 1, sm: 2 },
          minWidth: 0,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
